import { Suspense, lazy, useEffect, useRef, useState, type ReactNode } from 'react';
import { useQuery } from '@tanstack/react-query';
import { drawingsApi, punchApi, type PunchListResponse, type PunchPin, type PunchPriority } from '@/lib/apiClient';
import { resolveDownloadUrl } from '@/lib/graphUpload';
import { useGraphToken } from '@/lib/graphHooks';
import { randomId } from '@/lib/randomId';
import { useFocusTrap } from '@/lib/hooks/useFocusTrap';
import { shortDate, stepRoom, pushRecentRoom, roomTitle, cap } from './punchModel';
import { useObjectUrl, OutdoorToggle, VideoPicker, videoBlocks, type PickedVideo } from './punchUi';
import { uploadPunchVideo, addClipToSavedItem } from './punchVideos';
import { usePunchItemForm } from './usePunchItemForm';
import { AssigneeSelect, LeaderSelect } from './PunchAddItemModal';
import PunchDrawingBoundary from './PunchDrawingBoundary';
// The phone's drawing screen brings PDF.js with it: loaded the first time Pin on Drawing is tapped.
const PunchPhoneDrawing = lazy(() => import('./PunchPhoneDrawing'));

type Data = PunchListResponse & { list: NonNullable<PunchListResponse['list']> };

/** The design's "Tap to fill" phrases. */
const PHRASES = [
  'Touch up paint', 'Fill caulk line', 'Remove protective film', 'Replace ceiling tile',
  'Install missing cover plate', 'Patch drywall', 'Adjust door', 'Clean',
];

const STEPS = ['Photo', 'What needs fixing', 'Trade', 'Where'] as const;

/**
 * Add an item on a phone, one step at a time (#518, design "Add item: mobile,
 * step by step"): photo, what needs fixing, trade, where. Made to be quick and
 * one-handed on the walk; Save & Next goes straight back to the camera,
 * keeping the room and trade.
 *
 * The photo is taken with the phone's own camera (decided on #518), not an
 * in-app viewfinder. The form and its retry-safe save are the desktop's
 * (usePunchItemForm). The wall is in Notes, as on the desktop.
 *
 * A video clip is optional, on the last step (#518 phase 2). It needs the
 * item, so it goes up after the item is saved: if only the clip fails, the
 * item stays saved and the note says to add the clip from the item.
 *
 * Pin on Drawing, on the Where step, pins the item on a drawing with the phone's
 * crosshair and fills in the room read off the sheet, which can still be changed
 * (#518 phase 3, decided 2026-10-09). An item started from the drawing (Add Item
 * Here) arrives already pinned.
 */
export default function PunchPhoneAdd({ projectId, data, jobNumber, jobFolderUrl, initialRoom, pinned, onTop = false, onClose, onAdded }: {
  projectId: number;
  data: Data;
  jobNumber: string;
  jobFolderUrl: string | null;
  initialRoom?: string;
  /** Started from the phone's drawing (Add Item Here): the pin, the room read there, and where that is. */
  pinned?: { pin: PunchPin; room: string | null; where: string } | null;
  /** Opened over the phone's drawing screen, so it goes on top of it. */
  onTop?: boolean;
  onClose: () => void;
  onAdded: () => void;
}) {
  const [recent, setRecent] = useState<string[]>(() => readRecent(projectId));
  const f = usePunchItemForm({
    projectId, data, jobNumber, jobFolderUrl, onAdded,
    initialRoom: pinned?.room || (initialRoom ?? recent[0] ?? ''),
    initialPin: pinned?.pin ?? null,
  });
  // Pin on Drawing: the phone's drawing screen open over this form, and where the pin is, to say so.
  const [picking, setPicking] = useState(false);
  const [pinWhere, setPinWhere] = useState(pinned?.where ?? '');
  // Offered only on a job with drawings: the same list the drawing screen reads, so it is cached for it.
  const setsQ = useQuery({ queryKey: ['drawings', projectId], queryFn: () => drawingsApi.sets(projectId), staleTime: 30_000 });
  const hasDrawings = (setsQ.data?.length ?? 0) > 0;
  const [step, setStep] = useState(0);
  const [added, setAdded] = useState<string[]>([]);
  const [toast, setToast] = useState<string | null>(null);
  const [changeAssignee, setChangeAssignee] = useState(false);
  const [changeDefaults, setChangeDefaults] = useState(false);

  // The optional clip, its upload (0–1) while it goes up, and what to say if
  // the item saved but the clip didn't.
  const graph = useGraphToken();
  const [video, setVideo] = useState<PickedVideo | null>(null);
  const [videoProgress, setVideoProgress] = useState<number | null>(null);
  const [videoNote, setVideoNote] = useState<string | null>(null);
  // WeakMap, not Map: keyed by the File, so a clip or photo let go of is
  // freed rather than held for the whole session — a walk of Save & Next
  // could otherwise keep every clip picked (up to 500 MB each) alive (Codex on #593).
  const videoTags = useRef(new WeakMap<File, string>());
  const busy = f.busy || videoProgress != null;

  const camRef = useRef<HTMLInputElement>(null);
  const libRef = useRef<HTMLInputElement>(null);
  const photoUrl = useObjectUrl(f.photo);

  useEffect(() => {
    if (!toast) return;
    const t = window.setTimeout(() => setToast(null), 2800);
    return () => window.clearTimeout(t);
  }, [toast]);

  const pick = (file: File | null) => {
    if (!file) return;
    f.setPhoto(file);
    f.setError(null);
    setStep(1);
  };

  // Leaving drops a photo already taken and what was typed: ask first, as a
  // mis-tap on the walk would otherwise cost the photo.
  const close = () => {
    if (busy) return;
    if ((f.photo || f.description.trim() || f.notes.trim() || video)
      && !window.confirm('Discard this item? The photo and what you typed will be lost.')) return;
    onClose();
  };
  const back = () => (step === 0 ? close() : setStep((s) => s - 1));

  // A dialog over the list: focus stays inside, Escape leaves (asking first),
  // and the list behind doesn't scroll.
  const ref = useRef<HTMLDivElement>(null);
  // Paused while the drawing screen is open over it: Escape there steps out of the
  // drawing, not out of this form (which would ask to discard it).
  useFocusTrap(ref, { onEscape: close, paused: picking });
  useEffect(() => {
    const prev = document.body.style.overflow;
    document.body.style.overflow = 'hidden';
    return () => { document.body.style.overflow = prev; };
  }, []);

  const save = async (next: boolean) => {
    setVideoNote(null);
    const item = await f.save();
    if (!item) return;
    const label = item.label;
    let note: string | null = null;
    if (video) {
      // One tag per clip, so a retried upload reuses its file name.
      const tag = videoTags.current.get(video.file) ?? randomId().slice(0, 8);
      videoTags.current.set(video.file, tag);
      setVideoProgress(0);
      note = await addClipToSavedItem({
        item,
        file: video.file,
        upload: (file, itemNo) => uploadPunchVideo({
          file, itemNo, recorded: video.recorded, jobNumber, jobFolderUrl: jobFolderUrl!, subfolder: data.photoSubfolder,
          getToken: graph.interactive, tag, onProgress: (sent, total) => setVideoProgress(total ? sent / total : 0),
        }),
        addMedia: (itemId, clip) => punchApi.addMedia(projectId, itemId, 'video', clip),
      });
      setVideoProgress(null);
      if (!note) onAdded();
      setVideo(null);
    }
    const nextRecent = pushRecentRoom(recent, f.room);
    setRecent(nextRecent);
    writeRecent(projectId, nextRecent);
    // Saved with a clip that didn't go: stay, so the note is read.
    if (!next && !note) { onClose(); return; }
    setAdded((a) => [...a, label]);
    setVideoNote(note);
    setToast(`${label} saved${f.room.trim() ? ` · ${roomTitle(f.room)}` : ''}${next ? ' — next photo' : ''}`);
    f.reset(true);
    // Each item starts from its trade's default sub: one picked by hand for
    // this item doesn't carry on to the next (Codex on #569).
    f.setAssignee('default');
    setChangeAssignee(false);
    setStep(0);
  };

  const list = data.list;
  const dueText = list.dueMode === 'fixed'
    ? (list.dueDate ? `Due ${shortDate(list.dueDate)}` : 'No due date')
    : `Due in ${list.dueDays} day${list.dueDays === 1 ? '' : 's'}`;
  const tradeLabel = f.trades.find((t) => t.key === f.trade)?.label ?? '';
  const assigneeText = f.assignee === 'default'
    ? (f.mappedSub ?? 'Nobody yet (no trade default)')
    : f.assignee === 'none' ? 'Unassigned' : f.assignee === 'ew_crew' ? data.ewCrewLabel : f.assignee.slice(4);

  return (
    <div ref={ref} className={`pl-padd${onTop ? ' on-top' : ''}`} role="dialog" aria-modal="true" aria-label="Add punch item">
      <div className="pl-padd-hd">
        <button type="button" className="pl-phone-icon" onClick={back} disabled={busy} aria-label={step === 0 ? 'Close' : 'Back'}>‹</button>
        <div className="pl-padd-title">
          <strong>Add item</strong>
          <span className="pl-muted">Step {step + 1} of 4 · {STEPS[step]}</span>
        </div>
        <OutdoorToggle />
        <button type="button" className="pl-link" onClick={close} disabled={busy}>Cancel</button>
      </div>
      <div className="pl-padd-progress" aria-hidden>
        {STEPS.map((s, i) => <span key={s} className={i <= step ? 'done' : undefined} />)}
      </div>

      <div className="pl-padd-bd">
        {added.length > 0 && step === 0 && (
          <div className="pl-padd-added">{added.length} added · last {added[added.length - 1]}</div>
        )}

        {step === 0 && (
          <div className="pl-padd-cam">
            <div className="pl-padd-cam-title">Before photo <span className="pl-padd-req">Required</span></div>
            {f.room.trim() && <div className="pl-padd-roomchip">{roomTitle(f.room)} · last used</div>}
            <button type="button" className="pl-phone-btn primary wide tall" onClick={() => camRef.current?.click()}>📷 Take Photo</button>
            <button type="button" className="pl-phone-btn secondary wide" onClick={() => libRef.current?.click()}>Choose from Library</button>
            <input ref={camRef} type="file" accept="image/*" capture="environment" hidden
              onChange={(e) => { pick(e.target.files?.[0] ?? null); e.target.value = ''; }} />
            <input ref={libRef} type="file" accept="image/*" hidden
              onChange={(e) => { pick(e.target.files?.[0] ?? null); e.target.value = ''; }} />
          </div>
        )}

        {step === 1 && (
          <>
            <div className="pl-row">
              {photoUrl && <img className="pl-padd-thumb" src={photoUrl} alt="Before photo" />}
              <button type="button" className="pl-link" onClick={() => setStep(0)}>Retake</button>
            </div>
            <label className="pl-field"><span>What needs fixing?</span>
              <textarea className="pl-padd-text" value={f.description} maxLength={1000} autoFocus
                placeholder="e.g. Fill caulk line along the south wall" onChange={(e) => f.setDescription(e.target.value)} />
            </label>
            <div className="pl-caption">Tap to fill</div>
            <div className="pl-phone-chips wrap">
              {PHRASES.map((p) => (
                <button key={p} type="button" className="pl-phone-chip"
                  onClick={() => f.setDescription((d) => (d.trim() ? `${d.trim()}, ${p.toLowerCase()}` : p))}>
                  {p}
                </button>
              ))}
            </div>
            <button type="button" className="pl-phone-btn primary wide tall" disabled={!f.description.trim()}
              onClick={() => { f.setError(null); setStep(2); }}>
              Next
            </button>
          </>
        )}

        {step === 2 && (
          <>
            <div className="pl-caption">Which trade?</div>
            <div className="pl-trade-tiles">
              {f.trades.map((t) => (
                <button key={t.key} type="button" className="pl-trade-tile" aria-pressed={t.key === f.trade}
                  // Switching trade brings that trade's default sub; tapping the trade
                  // already chosen keeps an Assigned to picked by hand — this tap is
                  // also the only way on to the next step (Codex on #569).
                  onClick={() => { if (t.key !== f.trade) f.setAssignee('default'); f.setTrade(t.key); setStep(3); }}>
                  {t.label}
                </button>
              ))}
            </div>
            <AssignedCard text={assigneeText} open={changeAssignee} onToggle={() => setChangeAssignee((v) => !v)}>
              <AssigneeSelect f={f} data={data} className="pl-phone-select" />
            </AssignedCard>
          </>
        )}

        {step === 3 && (
          <>
            <div className="pl-caption">Room or area</div>
            <div className="pl-room-stepper">
              <button type="button" className="pl-phone-icon big" aria-label="Previous room" onClick={() => f.setRoom(stepRoom(f.room, -1))}>−</button>
              <input className="pl-room-value" value={f.room} maxLength={60} placeholder="104" aria-label="Room or area"
                onChange={(e) => f.setRoom(e.target.value)} />
              <button type="button" className="pl-phone-icon big" aria-label="Next room" onClick={() => f.setRoom(stepRoom(f.room, 1))}>＋</button>
            </div>
            {recent.length > 0 && (
              <div className="pl-phone-chips wrap" role="group" aria-label="Recent rooms">
                {recent.map((r) => (
                  <button key={r} type="button" className={`pl-phone-chip${r === f.room.trim() ? ' active' : ''}`}
                    aria-pressed={r === f.room.trim()} onClick={() => f.setRoom(r)}>
                    {r}
                  </button>
                ))}
              </div>
            )}
            {f.pin ? (
              <div className="pl-note pl-row" style={{ justifyContent: 'space-between' }}>
                <span>📍 Pinned on {pinWhere || 'a drawing'}</span>
                <span className="pl-row">
                  {hasDrawings && <button type="button" className="pl-link" disabled={busy} onClick={() => setPicking(true)}>Move</button>}
                  <button type="button" className="pl-link" disabled={busy} onClick={() => f.setPin(null)}>Don’t pin</button>
                </span>
              </div>
            ) : hasDrawings && (
              <button type="button" className="pl-phone-btn secondary wide" disabled={busy} onClick={() => setPicking(true)}>📍 Pin on Drawing</button>
            )}
            <label className="pl-field"><span>Notes</span>
              <textarea className="pl-padd-text small" value={f.notes} maxLength={1000}
                placeholder="Which wall, access — anything else" onChange={(e) => f.setNotes(e.target.value)} />
            </label>
            {f.leaders.length > 1 && (
              <label className="pl-field"><span>Field leader</span>
                <LeaderSelect f={f} className="pl-phone-select" />
              </label>
            )}
            <VideoPicker label="Video · optional" picked={video} onChange={setVideo} busy={busy} />
            {videoProgress != null && (
              <div className="pl-row">
                <div className="pl-bar" role="progressbar" aria-label="Uploading the video"
                  aria-valuenow={Math.round(videoProgress * 100)} aria-valuemin={0} aria-valuemax={100}>
                  <span style={{ width: `${Math.round(videoProgress * 100)}%` }} />
                </div>
                <span className="pl-muted">Uploading the video… {Math.round(videoProgress * 100)}%</span>
              </div>
            )}
            <div className="pl-padd-defaults">
              <span>{tradeLabel} · {assigneeText}</span>
              <span>
                {f.due ? `Due ${shortDate(f.due)}` : dueText} · {cap(f.priority || list.defaultPriority)} priority
                {f.leaders.length === 1 ? ` · ${f.leaders[0].name}` : ''}
              </span>
              <button type="button" className="pl-link" onClick={() => setChangeDefaults((v) => !v)}>
                {changeDefaults ? 'Done' : 'Change'}
              </button>
            </div>
            {changeDefaults && (
              <div className="pl-grid2">
                <label className="pl-field"><span>Due</span>
                  <input className="pl-phone-select" type="date" value={f.due} onChange={(e) => f.setDue(e.target.value)} />
                </label>
                <label className="pl-field"><span>Priority</span>
                  <select className="pl-phone-select" value={f.priority} onChange={(e) => f.setPriority(e.target.value as PunchPriority | '')}>
                    <option value="">List default</option>
                    <option value="low">Low</option><option value="normal">Normal</option><option value="high">High</option>
                  </select>
                </label>
              </div>
            )}
          </>
        )}

        {f.subsQ.isError && !f.error && (
          <div className="pl-error">Couldn’t load this job’s subs and field leaders. Close and try again.</div>
        )}
        {f.error && <div className="pl-error" role="alert">{f.error}</div>}
        {videoNote && <div className="pl-error" role="alert">{videoNote}</div>}
      </div>

      {step === 3 && (
        <div className="pl-padd-ft">
          <button type="button" className="pl-phone-btn secondary" disabled={busy || !f.subsQ.isSuccess || videoBlocks(video)} onClick={() => save(false)}>
            Save
          </button>
          <button type="button" className="pl-phone-btn primary" disabled={busy || !f.subsQ.isSuccess || videoBlocks(video)} onClick={() => save(true)}>
            {busy ? 'Saving…' : '📷 Save & Next'}
          </button>
        </div>
      )}

      {toast && <div className="pl-toast" role="status" aria-live="polite">✓ {toast}</div>}

      {picking && (
        <PunchDrawingBoundary onClose={() => setPicking(false)}>
          <Suspense fallback={null}>
            <PunchPhoneDrawing
              projectId={projectId}
              data={data}
              canWork
              overAdd
              pick={{
                current: f.pin,
                // The room read off the sheet fills the room, which can still be changed.
                onPick: (pin, room, where) => { f.setPin(pin); if (room) f.setRoom(room); setPinWhere(where); setPicking(false); },
              }}
              // Silent here, as on the desktop: the screen offers its own Sign In button.
              getPdfUrl={(set) => resolveDownloadUrl(set.webUrl, graph.silent)}
              needsSignIn={graph.needsSignIn}
              onSignIn={graph.interactive}
              onOpenItem={() => undefined}
              onAddAt={() => undefined}
              onChanged={() => undefined}
              onClose={() => setPicking(false)}
            />
          </Suspense>
        </PunchDrawingBoundary>
      )}
    </div>
  );
}

function AssignedCard({ text, open, onToggle, children }: {
  text: string; open: boolean; onToggle: () => void; children: ReactNode;
}) {
  return (
    <div className="pl-card pl-padd-assigned">
      <div className="pl-row" style={{ justifyContent: 'space-between' }}>
        <span><span className="pl-caption">Assigned to</span><br />{text}</span>
        <button type="button" className="pl-link" onClick={onToggle}>{open ? 'Done' : 'Change'}</button>
      </div>
      {open && children}
    </div>
  );
}

// Recent rooms, per job, for this browser tab's session only. Storage can be
// unavailable (private mode, blocked site data), so every access is guarded.
const recentKey = (projectId: number) => `punch-recent-rooms:${projectId}`;

function readRecent(projectId: number): string[] {
  try {
    const v = JSON.parse(sessionStorage.getItem(recentKey(projectId)) ?? '[]');
    return Array.isArray(v) ? v.filter((x): x is string => typeof x === 'string') : [];
  } catch {
    return [];
  }
}

function writeRecent(projectId: number, rooms: string[]) {
  try {
    sessionStorage.setItem(recentKey(projectId), JSON.stringify(rooms));
  } catch {
    /* not kept — fine */
  }
}
